import axios from 'axios';
import { ApplicationUser } from 'types/company/applicationUser';
import { axiosAppServicesWithStatus, fetcher } from 'utils/axios';

function parseInviteErrorBody(body: unknown): string | undefined {
  if (typeof body === 'string' && body.trim()) return body;
  if (body && typeof body === 'object' && 'message' in body) {
    const m = (body as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return undefined;
}

export type SendInviteEmailResult =
  | { result: true; [key: string]: unknown }
  | { result: false; httpStatus?: number; message?: string }
  | null;

export const getFunnerUserStatus = async (phoneNumber: string) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/services/GetFunnerUserStatus',
        {
          data: {
            phoneNumber,
          },
        },
      ],
      'history',
    );

    if (data)
      return data as {
        result: boolean;
        isOTPverified?: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('Error in getFunnerUserStatus:', error);
    return null;
  }
};
export const sendOTPByPhone = async (phoneNumber: string) => {
  try {
    const data = await fetcher('get', `/api/auth/resendOTPByPhone/${phoneNumber}`, 'history');

    if (data)
      return data as {
        data: any;
      };
    else return null;
  } catch (error) {
    console.warn('Error in sendOTPByPhone:', error);
    return null;
  }
};
export const fetchVerifyOTP = async (PhoneNumber: string, OTP: string) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/auth/VerifyOTPByPhoneNumber',
        {
          data: {
            PhoneNumber,
            OTP,
          },
        },
      ],
      'history',
    );
    if (data)
      return data as {
        success: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('Error in fetchVerifyOTP:', error);
    return null;
  }
};
export const addFunnerUserCompany = async (phone: string, otp: string) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/services/addFunnerUserCompaniesList',
        {
          data: {
            phone,
            otp,
          },
        },
      ],
      'history',
    );
    if (data) return Boolean(data) as boolean;
    else return null;
  } catch (error) {
    console.warn('Error in addFunnerUserCompany:', error);
    return null;
  }
};
export const addCompanyFunnerUser = async (params: {
  Email: string;
  Password: string;
  PhoneNumber: string;
  FirstName: string;
  LastName: string;
}) => {
  try {
    const data = await fetcher('post', ['/api/auth/addCompanyFunnerUser', { data: params }], 'history');
    if (data)
      return data as {
        result: boolean;
      };
    else return null;
  } catch (error) {
    console.warn('Error in addCompanyFunnerUser:', error);
    return null;
  }
};
/**
 * עדכון שם משתמש דרך הנוד: נכתב קודם לקלרק (מקור האמת לשמות — הסנכרון ב-app3
 * דורס עריכה מקומית) ואז ל-DB. אדמין, או המשתמש על עצמו.
 */
export const updateUserProfileName = async (
  userId: string,
  params: { firstName?: string; lastName?: string },
): Promise<{ result: boolean; firstName?: string; lastName?: string; fullName?: string } | null> => {
  try {
    const data = await fetcher(
      'put',
      ['/api/user-config/profile-name', { data: { userId, ...params } }],
      'servernode',
    );
    return (data as { result: boolean; firstName?: string; lastName?: string; fullName?: string }) ?? null;
  } catch (error) {
    console.warn('Error in updateUserProfileName:', error);
    return null;
  }
};

export const saveFunnerUserDetails = async (params: ApplicationUser) => {
  try {
    const data = await fetcher('post', ['/api/services/saveFunnerUserDetails', { data: params }], 'history');
    if (data) return data;
    else return null;
  } catch (error) {
    console.warn('Error in saveFunnerUserDetails:', error);
    return null;
  }
};
export const adminUpdateUserpic = async (formData: FormData) => {
  try {
    const data = await fetcher('post', ['/api/auth/adminUpdateUserpic', { data: formData }], 'history');
    if (data)
      return data as {
        result: boolean;
        image: string;
      };
    else return null;
  } catch (error) {
    console.warn('Error in adminUpdateUserpic:', error);
    return null;
  }
};
/** Backend expects camelCase property names matching C# model. */
export type SendInviteEmailRequestBody = {
  Email: string;
  FirstName: string;
  LastName: string;
  Role: string;
  CompanyId: string;
  frontendOrigin?: string;
};

export const sendInviteEmail = async (params: {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  companyId: string;
}): Promise<SendInviteEmailResult> => {
  try {
    const body: SendInviteEmailRequestBody = {
      Email: params.email,
      FirstName: params.firstName,
      LastName: params.lastName,
      Role: params.role,
      CompanyId: params.companyId,
      frontendOrigin: window.location.origin,
    };
    /** Invitation routes live on the app API host (same as `/api/users/me`), not `VITE_APP_HISTORY_API_URL`. */
    const res = await axiosAppServicesWithStatus.post('/api/invitation/sendInviteEmail', body);
    const data = res.data;
    if (data != null && typeof data === 'object') {
      return data as SendInviteEmailResult;
    }
    return null;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = parseInviteErrorBody(error.response?.data);
      return { result: false, httpStatus: status, message };
    }
    return null;
  }
};

export const getPendingApplicationUsers = async () => {
  try {
    /** `fetcher` מקבל אחרי interceptor רק את גוף התשובה — בלי סטטוס HTTP, אז 404 לא מזוהה ומציף console.error. כאן נשמר AxiosError מלא. */
    const res = await axiosAppServicesWithStatus.get('/api/invitation/getInvitations');
    const data = res.data;
    const invitations: Record<string, unknown>[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.users)
        ? data.users
        : Array.isArray(data?.pendingUsers)
          ? data.pendingUsers
          : Array.isArray(data?.data)
            ? data.data
            : [];

    return invitations.filter((invitation: Record<string, unknown>) => {
      const rawStatus = invitation?.status ?? invitation?.Status;
      return !(typeof rawStatus === 'string' && rawStatus.trim().toLowerCase() === 'accepted');
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return [];
    }
    console.warn('Error in getPendingApplicationUsers:', error);
    return [];
  }
};

type PendingInvitationActionParams = {
  invitationId?: string;
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  role?: string;
  companyId?: string;
  status?: string;
};

type PendingInvitationActionResponse = {
  result?: boolean;
  message?: string;
  error?: string;
  [key: string]: unknown;
};

const postPendingInvitationAction = async (
  endpoint: string,
  params: PendingInvitationActionParams,
): Promise<PendingInvitationActionResponse | null> => {
  try {
    const data = await fetcher('post', [endpoint, { data: { ...params, frontendOrigin: window.location.origin } }], 'app');

    if (data) return data as PendingInvitationActionResponse;
    return null;
  } catch (error) {
    console.warn(`Error in ${endpoint}:`, error);
    return null;
  }
};

export const resendPendingApplicationUserInvite = async (params: PendingInvitationActionParams) =>
  postPendingInvitationAction('/api/invitation/resendInvite', params);

export const cancelPendingApplicationUserInvite = async (params: PendingInvitationActionParams) =>
  postPendingInvitationAction('/api/invitation/cancelInvite', params);

export const removePendingApplicationUserInvite = async (params: PendingInvitationActionParams) =>
  postPendingInvitationAction('/api/invitation/removeInvite', params);

export const saveUserPermissionsConfig = async (userId: string, config: Record<string, boolean>): Promise<void> => {
  try {
    await fetcher('put', ['/api/user-config/permissions', { data: { userId, config: JSON.stringify(config) } }], 'servernode');
  } catch (error) {
    console.warn('Error in saveUserPermissionsConfig:', error);
  }
};

export const saveUserViewsConfig = async (userId: string, config: Record<string, boolean>): Promise<void> => {
  try {
    await fetcher('put', ['/api/user-config/views', { data: { userId, config: JSON.stringify(config) } }], 'servernode');
  } catch (error) {
    console.warn('Error in saveUserViewsConfig:', error);
  }
};

/**
 * הצגה/הסתרה של תצוגה מותאמת של משתמש אחר (אדמין בלבד) — עובר דרך הנוד,
 * שמעדכן את ProfileConfig של הבעלים בצד השרת ושולח לו סוקט userConfig:views.
 * מחזיר את ה-ProfileConfig המעודכן (JSON) לעדכון ה-store, או null בכשל.
 */
export const saveCustomViewVisibility = async (
  userId: string,
  viewId: string,
  visible: boolean,
): Promise<string | null> => {
  try {
    const data = await fetcher(
      'put',
      ['/api/user-config/custom-view-visibility', { data: { userId, viewId, visible } }],
      'servernode',
    );
    return typeof data?.profileConfig === 'string' ? data.profileConfig : null;
  } catch (error) {
    console.warn('Error in saveCustomViewVisibility:', error);
    return null;
  }
};
